/**
 * File Upload Configuration Utilities
 *
 * Provides centralized configuration for file upload limits and supported types.
 * Configuration is loaded from environment variables with sensible defaults.
 *
 * @module fileUploadConfig
 */

export interface FileUploadConfig {
    /** Maximum file size in megabytes (deprecated - use category-specific limits) */
    maxFileSizeMB: number;
    /** Maximum file size in bytes (deprecated - use category-specific limits) */
    maxFileSizeBytes: number;
    /** Category-specific file size limits in MB */
    maxImageSizeMB: number;
    maxDocumentSizeMB: number;
    maxVideoSizeMB: number;
    /** Maximum total combined size for all attachments in MB */
    maxTotalSizeMB: number;
    /** Supported file types configuration */
    supportedFileTypes: {
        /** Allowed file extensions (e.g., ['.pdf', '.jpg']) */
        extensions: string[];
        /** Allowed MIME types (e.g., ['application/pdf', 'image/jpeg']) */
        mimeTypes: string[];
        /** HTML input accept attribute value */
        accept: string;
        /** Image file extensions */
        imageExtensions: string[];
        /** Document file extensions */
        documentExtensions: string[];
        /** Video file extensions */
        videoExtensions: string[];
    };
}

/**
 * Get file upload configuration.
 *
 * File size limits per category:
 * - Images: 10 MB (.jpg, .jpeg, .png, .gif, .bmp)
 * - Documents: 20 MB (.pdf, .doc, .docx, .xls, .xlsx, .txt, .csv)
 * - Media/Videos: 25 MB (.mp4, .mov, .avi, .wmv, .flv, .mkv, .webm)
 * - Total combined: 60 MB for all attachments
 *
 * @returns {FileUploadConfig} File upload configuration object
 *
 * @example
 * ```typescript
 * const config = getFileUploadConfig();
 * logger.info(config.maxImageSizeMB); // 10
 * logger.info(config.maxDocumentSizeMB); // 20
 * logger.info(config.maxVideoSizeMB); // 25
 * logger.info(config.maxTotalSizeMB); // 60
 * ```
 */
export const getFileUploadConfig = (): FileUploadConfig => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp"];
    const documentExtensions = [
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".txt",
        ".csv",
    ];
    const videoExtensions = [
        ".mp4",
        ".mov",
        ".avi",
        ".wmv",
        ".flv",
        ".mkv",
        ".webm",
    ];

    return {
        // Deprecated - use category-specific limits
        maxFileSizeMB: 20,
        maxFileSizeBytes: 20 * 1024 * 1024,

        // Category-specific limits (per requirements)
        maxImageSizeMB: 10,
        maxDocumentSizeMB: 20,
        maxVideoSizeMB: 25,
        maxTotalSizeMB: 60,

        supportedFileTypes: {
            extensions: [
                ...imageExtensions,
                ...documentExtensions,
                ...videoExtensions,
            ],
            mimeTypes: [
                // Images
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/gif",
                "image/bmp",
                // Documents
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                // Text
                "text/plain",
                "text/csv",
                "application/csv",
                // Videos
                "video/mp4",
                "video/quicktime",
                "video/x-msvideo",
                "video/x-ms-wmv",
                "video/x-flv",
                "video/x-matroska",
                "video/webm",
            ],
            accept: ".jpg,.jpeg,.png,.gif,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm",
            imageExtensions,
            documentExtensions,
            videoExtensions,
        },
    };
};

/**
 * Get file category based on file extension.
 *
 * @param {string} filename - File name with extension
 * @returns {"image" | "document" | "video" | "unknown"} File category
 *
 * @example
 * ```typescript
 * getFileCategory("photo.jpg");     // "image"
 * getFileCategory("report.pdf");    // "document"
 * getFileCategory("video.mp4");     // "video"
 * ```
 */
export const getFileCategory = (
    filename: string
): "image" | "document" | "video" | "unknown" => {
    const config = getFileUploadConfig();
    const extension = filename
        .toLowerCase()
        .substring(filename.lastIndexOf("."));

    if (config.supportedFileTypes.imageExtensions.includes(extension)) {
        return "image";
    }
    if (config.supportedFileTypes.documentExtensions.includes(extension)) {
        return "document";
    }
    if (config.supportedFileTypes.videoExtensions.includes(extension)) {
        return "video";
    }
    return "unknown";
};

/**
 * Get maximum file size for a specific file based on its category.
 *
 * @param {string} filename - File name with extension
 * @returns {number} Maximum file size in bytes
 *
 * @example
 * ```typescript
 * getMaxFileSizeForFile("photo.jpg");   // 10485760 (10MB)
 * getMaxFileSizeForFile("report.pdf");  // 20971520 (20MB)
 * getMaxFileSizeForFile("video.mp4");   // 26214400 (25MB)
 * ```
 */
export const getMaxFileSizeForFile = (filename: string): number => {
    const config = getFileUploadConfig();
    const category = getFileCategory(filename);

    switch (category) {
        case "image":
            return config.maxImageSizeMB * 1024 * 1024;
        case "document":
            return config.maxDocumentSizeMB * 1024 * 1024;
        case "video":
            return config.maxVideoSizeMB * 1024 * 1024;
        default:
            return config.maxDocumentSizeMB * 1024 * 1024; // Default to document limit
    }
};

/**
 * Format bytes to human-readable file size string.
 *
 * Converts byte values to appropriate unit (B, KB, MB, GB) with 2 decimal places.
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB", "250 KB")
 *
 * @example
 * ```typescript
 * formatFileSize(0);              // "0 B"
 * formatFileSize(1024);           // "1 KB"
 * formatFileSize(1536);           // "1.5 KB"
 * formatFileSize(5242880);        // "5 MB"
 * formatFileSize(26214400);       // "25 MB"
 * ```
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
